import React, { useEffect, useRef } from 'react';

/* ── FADE-IN ON SCROLL ─────────────────────────── */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ─────────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────────── */
function About() {
  const ref = useFadeIn();
  return (
    <section id="about" className="about-section">
      <div className="section-inner" ref={ref}>
        <p className="section-label">Who I Am</p>
        <h2 className="section-title">About Me</h2>
        <div className="section-line" />

        <div className="about-grid">
          <div className="about-text">
            <p>
              I'm <strong>Madhumitha V</strong>, an Associate Software Engineer (Front-end) based in
              Chennai with <strong>2+ years of industry experience</strong> at Hurix Systems. I
              specialize in building scalable, performant, and accessible web applications using
              modern JavaScript technologies.
            </p>
            <p>
              My work spans <strong>ReactJS, TypeScript, Redux, and the MERN Stack</strong>. I've
              contributed to enterprise-level eLearning platforms, AI-powered authoring tools, and
              accessibility-focused products — always striving for high code quality through unit
              testing, CI/CD, and Agile delivery.
            </p>
            <p>
              I hold a <strong>Guinness World Record Certificate</strong> for a Face Recognition
              Application built with Python, and was recognized with a <strong>Spot Award</strong>{' '}
              for the successful delivery of the IMS India project.
            </p>

            <div className="about-info">
              {[
                { label: 'Location',  value: 'Chennai, India' },
                { label: 'Experience', value: '2+ Years' },
                { label: 'Role',      value: 'Frontend / MERN Dev' },
                { label: 'Education', value: 'B.E — Rajalakshmi Engg.' },
                { label: 'Phone',     value: '+91 9841240166' },
                { label: 'Languages', value: 'English · Tamil · Telugu' },
              ].map((item) => (
                <div className="info-item" key={item.label}>
                  <div className="info-label">{item.label}</div>
                  <div className="info-value">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="about-highlights">
            {[
              {
                icon: '💡',
                title: 'Problem Solver',
                desc: 'I thrive on debugging complex issues, optimizing performance, and finding elegant solutions to challenging UI problems.',
              },
              {
                icon: '🤝',
                title: 'Team Player & Leader',
                desc: 'Experienced in Agile/Scrum environments, code reviews, pull requests, and cross-functional collaboration with design, QA, and product teams.',
              },
              {
                icon: '♿',
                title: 'Accessibility Champion',
                desc: 'Contributed to EqualSense — an AI-powered alt-text generator — and ensured WCAG compliance across multiple projects for inclusive UX.',
              },
              {
                icon: '🚀',
                title: 'Continuous Learner',
                desc: 'Completed AI Full Stack Engineer training, MERN Stack certification, prompt engineering basics, and Udemy web development bootcamp.',
              },
            ].map((h) => (
              <div className="highlight-card" key={h.title}>
                <div className="highlight-icon">{h.icon}</div>
                <div>
                  <div className="highlight-title">{h.title}</div>
                  <div className="highlight-desc">{h.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   SKILLS
───────────────────────────────────────────────── */
const SKILL_CATEGORIES = [
  {
    icon: '⚛️',
    title: 'Frontend',
    skills: [
      { name: 'ReactJS', primary: true },
      { name: 'TypeScript', primary: true },
      { name: 'JavaScript (ES6+)', primary: true },
      { name: 'HTML5', primary: true },
      { name: 'CSS3', primary: true },
      { name: 'Redux', primary: false },
      { name: 'Context API', primary: false },
      { name: 'React Hooks', primary: false },
    ],
  },
  {
    icon: '🛠️',
    title: 'State & Testing',
    skills: [
      { name: 'Redux Toolkit', primary: true },
      { name: 'Jest', primary: true },
      { name: 'React Testing Library', primary: true },
      { name: 'Unit Testing', primary: false },
      { name: 'Accessibility (WCAG)', primary: false },
      { name: 'Cross-Browser Testing', primary: false },
    ],
  },
  {
    icon: '🌐',
    title: 'MERN Stack',
    skills: [
      { name: 'MongoDB', primary: true },
      { name: 'Express.js', primary: true },
      { name: 'Node.js', primary: true },
      { name: 'REST APIs', primary: false },
      { name: 'Python (Basics)', primary: false },
    ],
  },
  {
    icon: '⚙️',
    title: 'DevOps & Tools',
    skills: [
      { name: 'Git', primary: true },
      { name: 'CI/CD Pipelines', primary: true },
      { name: 'VS Code', primary: false },
      { name: 'SonarQube', primary: false },
      { name: 'Jira', primary: false },
    ],
  },
  {
    icon: '🤖',
    title: 'AI Tools',
    skills: [
      { name: 'GitHub Copilot', primary: true },
      { name: 'Claude AI', primary: true },
      { name: 'ChatGPT', primary: false },
      { name: 'Krea AI', primary: false },
      { name: 'Prompt Engineering', primary: false },
    ],
  },
  {
    icon: '🏆',
    title: 'Methodologies',
    skills: [
      { name: 'Agile / Scrum', primary: true },
      { name: 'Code Reviews', primary: false },
      { name: 'Pull Requests', primary: false },
      { name: 'Pair Programming', primary: false },
      { name: 'Performance Optimization', primary: false },
    ],
  },
];

function Skills() {
  const ref = useFadeIn();
  return (
    <section id="skills" className="skills-section">
      <div className="section-inner" ref={ref}>
        <p className="section-label">What I Know</p>
        <h2 className="section-title">Technical Skills</h2>
        <div className="section-line" />
        <p className="section-desc">
          A full-stack of modern web technologies, with deep expertise in React ecosystem and
          growing proficiency across the MERN stack.
        </p>

        <div className="skills-grid">
          {SKILL_CATEGORIES.map((cat) => (
            <div className="skill-category" key={cat.title}>
              <div className="skill-cat-icon">{cat.icon}</div>
              <div className="skill-cat-title">{cat.title}</div>
              <div className="skill-tags">
                {cat.skills.map((s) => (
                  <span
                    key={s.name}
                    className={`skill-tag ${s.primary ? 'primary' : ''}`}
                  >
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   EXPERIENCE
───────────────────────────────────────────────── */
function Experience() {
  const ref = useFadeIn();
  return (
    <section id="experience" className="experience-section">
      <div className="section-inner" ref={ref}>
        <p className="section-label">My Journey</p>
        <h2 className="section-title">Work Experience</h2>
        <div className="section-line" />

        <div className="timeline">
          {/* ── Role 1 ── */}
          <div className="timeline-item">
            <div className="timeline-dot" />
            <div className="timeline-card">
              <div className="timeline-header">
                <div>
                  <div className="timeline-role">Associate Software Engineer</div>
                  <div className="timeline-company">Hurix Systems Private Limited</div>
                </div>
                <div className="timeline-period">Oct 2024 – Aug 2025</div>
              </div>
              <p className="timeline-desc">
                Hurix Systems — a leader in digital learning solutions and eLearning technology —
                driving innovation in scalable, accessible software applications.
              </p>
              <ul className="timeline-bullets">
                <li>
                  Developed responsive UIs using ReactJS, TypeScript, HTML5, CSS3, and JavaScript
                  (ES6+), with efficient state management using Hooks, Redux, and Context API.
                </li>
                <li>
                  Collaborated using Git, VS Code, and SonarQube for version control, code quality,
                  and continuous inspection; contributed to Agile sprints tracked in Jira.
                </li>
                <li>
                  Performed unit testing with Jest and React Testing Library; optimized performance,
                  implemented CI/CD pipelines, and ensured cross-browser compatibility.
                </li>
              </ul>

              <div className="timeline-projects">
                <div className="project-pill">
                  <div className="project-pill-name">
                    <span>📘</span> IMS India — Item Banking Project
                  </div>
                  <div className="project-pill-desc">
                    Built an authoring solution using ReactJS, Redux, TypeScript, and ES6. Developed
                    reusable front-end components and wrote unit tests with Jest. Delivered sprints
                    on time via Jira.
                  </div>
                </div>
                <div className="project-pill">
                  <div className="project-pill-name">
                    <span>🤖</span> Dictera — AI-Powered Course Creation
                  </div>
                  <div className="project-pill-desc">
                    Contributed to an AI/ML-driven eLearning tool by integrating CKEditor plugins
                    for course creation and manual editing workflows, improving authoring efficiency.
                  </div>
                </div>
                <div className="project-pill">
                  <div className="project-pill-name">
                    <span>♿</span> EqualSense — AI Alt-Text Generator
                  </div>
                  <div className="project-pill-desc">
                    Designed accessible UI with ReactJS, HTML5, and CSS3 for an AI-powered alt-text
                    generation tool. Ensured WCAG compliance and improved UX on image-intensive
                    platforms.
                  </div>
                </div>
                <div className="project-pill">
                  <div className="project-pill-name">
                    <span>🎓</span> AI Full Stack Capstone
                  </div>
                  <div className="project-pill-desc">
                    Built a video transcription &amp; summarization tool using ReactJS, Node.js,
                    MongoDB, and CI/CD pipelines. Gained full-stack experience end-to-end.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Role 2 ── */}
          <div className="timeline-item">
            <div className="timeline-dot" style={{ background: 'var(--teal)' }} />
            <div className="timeline-card">
              <div className="timeline-header">
                <div>
                  <div className="timeline-role">Trainee Software Engineer</div>
                  <div className="timeline-company">Hurix Systems Private Limited</div>
                </div>
                <div className="timeline-period">Jun 2023 – Sep 2024</div>
              </div>
              <p className="timeline-desc">
                Joined as a trainee and rapidly progressed, contributing to real-world eLearning
                projects from day one.
              </p>
              <ul className="timeline-bullets">
                <li>
                  Developed front-end components using HTML, CSS, TypeScript, and ReactJS with
                  version control tools.
                </li>
                <li>
                  Participated in Agile sprints, code reviews, pull requests, and testing processes
                  to ensure high-quality deliverables.
                </li>
                <li>
                  Gained hands-on experience in CI/CD workflows, debugging, and working
                  collaboratively in cross-functional teams.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   ACHIEVEMENTS
───────────────────────────────────────────────── */
function Achievements() {
  const ref = useFadeIn();
  return (
    <section id="achievements" className="achievements-section">
      <div className="section-inner" ref={ref}>
        <p className="section-label">Recognition &amp; Learning</p>
        <h2 className="section-title">Achievements &amp; Training</h2>
        <div className="section-line" />

        <div className="achievements-grid">
          <div className="achievement-card">
            <div className="achievement-icon">🏅</div>
            <div className="achievement-title">Spot Award</div>
            <div className="achievement-desc">
              Certificate of Appreciation from Hurix Systems for the successful and timely
              completion of the IMS India – Item Banking Project.
            </div>
          </div>
          <div className="achievement-card">
            <div className="achievement-icon">🌍</div>
            <div className="achievement-title">Guinness World Record</div>
            <div className="achievement-desc">
              Won the Guinness Record Certificate at the AI-For-Indian Event (GUVI) for building a
              Face Recognition Application using Python at school level.
            </div>
          </div>
          <div className="achievement-card">
            <div className="achievement-icon">♿</div>
            <div className="achievement-title">EqualSense Contributor</div>
            <div className="achievement-desc">
              Key contributor to an AI-powered alt-text generator, designing WCAG-compliant UI that
              improved accessibility for image-intensive platforms.
            </div>
          </div>
          <div className="achievement-card">
            <div className="achievement-icon">📈</div>
            <div className="achievement-title">Promoted in 16 Months</div>
            <div className="achievement-desc">
              Progressed from Trainee to Associate Software Engineer within 16 months, reflecting
              strong performance and consistent quality deliverables.
            </div>
          </div>
        </div>

        {/* Training / Courses */}
        <p className="section-label" style={{ marginTop: '4rem' }}>Certifications</p>
        <h3 className="section-title" style={{ fontSize: '1.8rem' }}>Training &amp; Courses</h3>
        <div className="section-line" />

        <div className="training-grid">
          {[
            {
              icon: '🧑‍💻',
              name: 'AI Full Stack Engineer Training',
              desc: 'Hands-on training in ReactJS, Node.js, MongoDB, Python, and CI/CD with front-end project focus.',
            },
            {
              icon: '🤖',
              name: 'AI For All – Prompt Engineering Basics',
              desc: 'Completed foundational training in prompt engineering and applied AI concepts.',
            },
            {
              icon: '🌐',
              name: 'MERN Stack Certification - FITA Academy',
              desc: 'Certified in MERN Stack (MongoDB, Express.js, ReactJS, and Node.js) with hands-on experience in developing scalable full-stack web applications, RESTful APIs, and database-driven solutions.',
            },
            {
              icon: '🎓',
              name: 'Complete Web Development Bootcamp',
              desc: 'Udemy 2023 certification covering the full spectrum of modern web development.',
            },
          ].map((t) => (
            <div className="training-card" key={t.name}>
              <div className="training-icon">{t.icon}</div>
              <div>
                <div className="training-name">{t.name}</div>
                <div className="training-desc">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   CONTACT
───────────────────────────────────────────────── */
function Contact() {
  const ref = useFadeIn();
  return (
    <section id="contact" className="contact-section">
      <div className="section-inner" ref={ref}>
        <p className="section-label">Let's Connect</p>
        <h2 className="section-title">Get In Touch</h2>
        <div className="section-line" />

        <div className="contact-grid">
          <div>
            <p className="section-desc">
              I'm actively looking for ReactJS / MERN Stack Developer roles. If you have an
              exciting opportunity or just want to say hello, feel free to reach out!
            </p>

            <div className="contact-links">
              <a href="tel:+919841240166" className="contact-link">
                <div className="contact-link-icon">📞</div>
                <div>
                  <div className="contact-link-label">Phone</div>
                  <div className="contact-link-value">+91 9841240166</div>
                </div>
              </a>
              <a href="mailto:madhuvenkat0101@gmail.com" className="contact-link">
                <div className="contact-link-icon">✉️</div>
                <div>
                  <div className="contact-link-label">Email</div>
                  <div className="contact-link-value">madhuvenkat0101@gmail.com</div>
                </div>
              </a>
              <a
                href="https://www.linkedin.com/in/madhumitha-01-v"
                target="_blank"
                rel="noreferrer"
                className="contact-link"
              >
                <div className="contact-link-icon">💼</div>
                <div>
                  <div className="contact-link-label">LinkedIn</div>
                  <div className="contact-link-value">linkedin.com/in/madhumitha-01-v</div>
                </div>
              </a>
              <div className="contact-link" style={{ cursor: 'default' }}>
                <div className="contact-link-icon">📍</div>
                <div>
                  <div className="contact-link-label">Location</div>
                  <div className="contact-link-value">Chennai, Tamil Nadu, India</div>
                </div>
              </div>
            </div>
          </div>

          <div className="resume-download-box">
            <div className="resume-icon">📄</div>
            <div className="resume-title">My Resume</div>
            <p className="resume-desc">
              Download my full resume to see my complete work history, project details, skills, and
              certifications in one place.
            </p>
            <a href="/Resume.pdf" download className="resume-btn">
              ⬇&nbsp; Download Resume (PDF)
            </a>
            <div className="resume-note">PDF · Updated 2025 · 2 pages</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="footer">
      <p>
        Designed &amp; built by <span>Madhumitha V</span> · Frontend &amp; MERN Stack Engineer ·
        Chennai, India
      </p>
      <p className="footer-sub">
        ReactJS · TypeScript · MERN Stack · Open to Work
      </p>
    </footer>
  );
}

/* ─────────────────────────────────────────────────
   EXPORT
───────────────────────────────────────────────── */
export { About, Skills, Experience, Achievements, Contact, Footer };
